import { Droplet, Droplets, Plus, ShieldAlert, Timer, Trash2 } from 'lucide-react';
import { IPD_BLOOD_PRODUCTS, IPD_TRANSFUSION_DEFAULT_MINUTES, ipdTransfusionMinutesPerUnit } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { FormSection } from '@/components/ui/form-screen';
import { Button, Field, Input, Switch, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DrugSearchInput } from '@/features/opd/DrugSearchInput';
import { ComputedValue, InstantPicker, Stepper } from './controls';
import type { FieldsProps } from './form-props';
import { durationText, everyLabel } from './order-labels';
import {
  IV_CHECK_INTERVALS,
  IV_VOLUME_CHOICES,
  ivDurationFromRate,
  ivRateFromDuration,
  minutesOf,
  numberOf,
  transfusionRateFromMinutes,
  type IvFluidDraft,
  type RateSetting,
  type TransfusionDraft,
} from './order-form';
import { StopAtField } from './ScheduleFields';

const MAX_ADDITIVES = 5;
let additiveCount = 0;

function additiveId(): string {
  additiveCount += 1;
  return `additive-${additiveCount}`;
}

function rateOptions(): { value: RateSetting; label: string }[] {
  return [
    { value: 'rate', label: tr('ipd', 'io_form_set_by_rate') },
    { value: 'duration', label: tr('ipd', 'io_form_set_by_duration') },
  ];
}

export function IvFluidFields({ draft, update, error, common, setCommon }: FieldsProps<IvFluidDraft>) {
  const volume = numberOf(draft.volumeMl) ?? Number.NaN;
  const computedMinutes = draft.setBy === 'rate' ? ivDurationFromRate(volume, numberOf(draft.rateMlPerHour) ?? Number.NaN) : null;
  const computedRate = draft.setBy === 'duration' ? ivRateFromDuration(volume, minutesOf(draft.durationHours, draft.durationMins) ?? Number.NaN) : null;
  const volumeChip = (IV_VOLUME_CHOICES as readonly number[]).map(String).includes(draft.volumeMl.trim()) ? draft.volumeMl.trim() : '';
  const setAdditive = (index: number, patch: Partial<IvFluidDraft['additives'][number]>) =>
    update({ additives: draft.additives.map((additive, at) => (at === index ? { ...additive, ...patch } : additive)) });

  return (
    <>
      <FormSection title={tr('ipd', 'io_form_section_fluid')} icon={Droplets}>
        <Field label={tr('ipd', 'io_form_fluid')} hint={tr('ipd', 'io_form_fluid_hint')} required error={error('fluid', 'productId')} className="sm:col-span-2">
          <DrugSearchInput
            value={draft.fluid}
            onType={(text) => update({ fluid: text, productId: null, controlled: false })}
            onChoose={(choice) => update({ fluid: choice.drugName, productId: choice.productId, controlled: Boolean(choice.isControlled) })}
          />
        </Field>
        <Field label={tr('ipd', 'io_form_volume_ml')} required error={error('volumeMl')} className="sm:col-span-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <ChoiceButtons layout="chips"
              aria-label={tr('ipd', 'io_form_volume_ml')}
              value={volumeChip}
              options={IV_VOLUME_CHOICES.map((choice) => ({ value: String(choice), label: String(choice) }))}
              onChange={(volumeMl) => update({ volumeMl })}
            />
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={5000}
              className="sm:w-32"
              aria-label={tr('ipd', 'io_form_volume_ml')}
              value={draft.volumeMl}
              onChange={(event) => update({ volumeMl: event.target.value })}
            />
          </div>
        </Field>
        <Field label={tr('ipd', 'io_form_additives')} error={error('additives')} className="sm:col-span-2">
          <div className="space-y-2">
            {draft.additives.map((additive, index) => (
              <div key={additive.id} className="flex items-start gap-2">
                <Input
                  className="min-w-0 flex-[2]"
                  aria-label={tr('ipd', 'io_form_additive_name')}
                  placeholder={tr('ipd', 'io_form_additive_name')}
                  maxLength={120}
                  value={additive.name}
                  onChange={(event) => setAdditive(index, { name: event.target.value })}
                />
                <Input
                  className="min-w-0 flex-1"
                  aria-label={tr('ipd', 'io_form_additive_amount')}
                  placeholder={tr('ipd', 'io_form_additive_amount')}
                  maxLength={40}
                  value={additive.amount}
                  onChange={(event) => setAdditive(index, { amount: event.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={tr('ipd', 'io_form_remove_additive')}
                  onClick={() => update({ additives: draft.additives.filter((_, at) => at !== index) })}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ))}
            {draft.additives.length < MAX_ADDITIVES && (
              <Button type="button" variant="outline" size="sm" onClick={() => update({ additives: [...draft.additives, { id: additiveId(), name: '', amount: '' }] })}>
                <Plus className="h-4 w-4" aria-hidden /> {tr('ipd', 'io_form_add_additive')}
              </Button>
            )}
          </div>
        </Field>
        <div className="sm:col-span-2">
          <Switch
            checked={draft.isHighAlert || draft.controlled}
            disabled={draft.controlled}
            onCheckedChange={(isHighAlert) => update({ isHighAlert })}
            label={
              <span className="inline-flex items-center gap-1.5">
                <ShieldAlert className="h-4 w-4 text-destructive" aria-hidden />
                {tr('ipd', 'io_form_iv_high_alert')}
              </span>
            }
            description={tr('ipd', draft.controlled ? 'io_form_controlled' : 'io_form_iv_high_alert_hint')}
          />
        </div>
      </FormSection>

      <FormSection title={tr('ipd', 'io_form_section_rate')} icon={Timer}>
        <div className="sm:col-span-2">
          <ChoiceButtons value={draft.setBy} label={tr('ipd', 'io_form_section_rate')} options={rateOptions()} onChange={(setBy) => update({ setBy })} />
        </div>
        {draft.setBy === 'rate' ? (
          <Field label={tr('ipd', 'io_form_rate_ml_h')} required error={error('rateMlPerHour', 'durationMinutes')}>
            <Input type="number" inputMode="decimal" min={1} max={2000} step="any" value={draft.rateMlPerHour} onChange={(event) => update({ rateMlPerHour: event.target.value })} />
          </Field>
        ) : (
          <Field label={tr('ipd', 'io_form_duration')} required error={error('durationMinutes', 'rateMlPerHour')}>
            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1 text-xs text-muted-foreground">
                <span>{tr('ipd', 'io_form_hours')}</span>
                <Input type="number" inputMode="numeric" min={0} max={48} value={draft.durationHours} onChange={(event) => update({ durationHours: event.target.value })} />
              </label>
              <label className="space-y-1 text-xs text-muted-foreground">
                <span>{tr('ipd', 'io_form_minutes')}</span>
                <Input type="number" inputMode="numeric" min={0} max={59} step={5} value={draft.durationMins} onChange={(event) => update({ durationMins: event.target.value })} />
              </label>
            </div>
          </Field>
        )}
        <div className="flex items-end">
          {computedMinutes !== null && <ComputedValue>{tr('ipd', 'io_form_runs_for', { duration: durationText(computedMinutes) })}</ComputedValue>}
          {computedRate !== null && <ComputedValue>{tr('ipd', 'io_form_rate_value', { rate: computedRate })}</ComputedValue>}
        </div>
        <Field label={tr('ipd', 'io_form_check_every')} className="sm:col-span-2">
          <ChoiceButtons layout="chips"
            value={String(draft.checkIntervalMinutes)}
            options={IV_CHECK_INTERVALS.map((minutes) => ({ value: String(minutes), label: everyLabel(minutes) }))}
            onChange={(value) => update({ checkIntervalMinutes: Number(value) })}
          />
        </Field>
      </FormSection>

      <FormSection title={tr('ipd', 'io_form_section_bags')} icon={Droplet}>
        <div className="sm:col-span-2">
          <Switch checked={draft.continuous} onCheckedChange={(continuous) => update({ continuous })} label={tr('ipd', 'io_form_continuous')} description={tr('ipd', 'io_form_continuous_hint')} />
        </div>
        {!draft.continuous && (
          <Field label={tr('ipd', 'io_form_bags')} error={error('bags')}>
            <Stepper value={draft.bags} min={1} max={20} onChange={(bags) => update({ bags })} />
          </Field>
        )}
        <Field label={tr('ipd', 'io_form_first_bag_at')} hint={tr('ipd', 'io_form_first_bag_hint')} hintBelow error={error('firstBagAt')}>
          <InstantPicker value={draft.firstBagAt} onChange={(firstBagAt) => update({ firstBagAt })} />
        </Field>
        {draft.continuous && <StopAtField common={common} setCommon={setCommon} error={error} />}
      </FormSection>
    </>
  );
}

export function TransfusionFields({ draft, update, error }: FieldsProps<TransfusionDraft>) {
  const volume = numberOf(draft.volumeMlPerUnit);
  const defaultMinutes = draft.product ? IPD_TRANSFUSION_DEFAULT_MINUTES[draft.product] : null;
  const perUnit = draft.product
    ? ipdTransfusionMinutesPerUnit({
        product: draft.product,
        volumeMlPerUnit: volume,
        rateMlPerHour: draft.setBy === 'rate' ? numberOf(draft.rateMlPerHour) : undefined,
        durationMinutesPerUnit: draft.setBy === 'duration' ? numberOf(draft.durationMinutesPerUnit) : undefined,
      })
    : null;
  const usablePerUnit = perUnit !== null && Number.isFinite(perUnit) ? perUnit : null;
  const rate = usablePerUnit !== null && volume !== undefined ? transfusionRateFromMinutes(volume, usablePerUnit) : null;

  return (
    <>
      <FormSection title={tr('ipd', 'io_form_section_blood')} icon={Droplet}>
        <Field label={tr('ipd', 'io_form_product')} required error={error('product')} className="sm:col-span-2">
          <ChoiceButtons layout="chips" value={draft.product} options={IPD_BLOOD_PRODUCTS.map((product) => ({ value: product, label: tr('ipd', `io_form_blood_${product}`) }))} onChange={(product) => update({ product })} />
        </Field>
        <Field label={tr('ipd', 'io_form_units')} error={error('units')}>
          <Stepper value={draft.units} min={1} max={10} onChange={(units) => update({ units })} />
        </Field>
        <Field label={tr('ipd', 'io_form_crossmatch')} error={error('crossmatchRef')}>
          <Input maxLength={60} value={draft.crossmatchRef} onChange={(event) => update({ crossmatchRef: event.target.value })} />
        </Field>
        <Field label={tr('ipd', 'io_form_indication')} error={error('indication')} className="sm:col-span-2">
          <Textarea rows={2} maxLength={400} value={draft.indication} onChange={(event) => update({ indication: event.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('ipd', 'io_form_section_rate')} icon={Timer}>
        <Field label={tr('ipd', 'io_form_volume_per_unit')} required={draft.setBy === 'rate'} error={error('volumeMlPerUnit')}>
          <Input type="number" inputMode="numeric" min={10} max={1000} value={draft.volumeMlPerUnit} onChange={(event) => update({ volumeMlPerUnit: event.target.value })} />
        </Field>
        <div className="sm:col-span-2">
          <ChoiceButtons value={draft.setBy} label={tr('ipd', 'io_form_section_rate')} options={rateOptions()} onChange={(setBy) => update({ setBy })} />
        </div>
        {draft.setBy === 'rate' ? (
          <Field label={tr('ipd', 'io_form_rate_ml_h')} required error={error('rateMlPerHour', 'durationMinutesPerUnit')}>
            <Input type="number" inputMode="decimal" min={1} max={1000} step="any" value={draft.rateMlPerHour} onChange={(event) => update({ rateMlPerHour: event.target.value })} />
          </Field>
        ) : (
          <Field
            label={tr('ipd', 'io_form_minutes_per_unit')}
            hint={defaultMinutes !== null ? tr('ipd', 'io_form_default_minutes', { minutes: defaultMinutes }) : undefined}
            hintBelow
            error={error('durationMinutesPerUnit', 'rateMlPerHour')}
          >
            <Input
              type="number"
              inputMode="numeric"
              min={10}
              max={480}
              placeholder={defaultMinutes !== null ? String(defaultMinutes) : undefined}
              value={draft.durationMinutesPerUnit}
              onChange={(event) => update({ durationMinutesPerUnit: event.target.value })}
            />
          </Field>
        )}
        <div className="space-y-2 sm:col-span-2">
          {usablePerUnit !== null && <ComputedValue>{tr('ipd', 'io_form_each_unit_runs', { duration: durationText(usablePerUnit) })}</ComputedValue>}
          {rate !== null && draft.setBy === 'duration' && <ComputedValue>{tr('ipd', 'io_form_rate_value', { rate })}</ComputedValue>}
        </div>
        <Field label={tr('ipd', 'io_form_first_unit_at')} hint={tr('ipd', 'io_form_first_unit_hint')} hintBelow error={error('firstUnitAt')}>
          <InstantPicker value={draft.firstUnitAt} onChange={(firstUnitAt) => update({ firstUnitAt })} />
        </Field>
      </FormSection>
    </>
  );
}
